/** Suntronix ONE — Service Group page.
 *  Lists every service inside a given outcome group.
 *  Route: /services/group/:group
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Sparkles, ShieldCheck, Clock, ChevronRight, Star,
} from "lucide-react";

function priceLabel(s) {
  const visibility = s.pricing_visibility || "show_price";
  if (visibility === "quotation_required") return "Quotation on request";
  if (visibility === "contact_suntronix") return "Contact Suntronix";
  if (visibility === "included_in_contract") return "Included in contract";
  if (visibility === "hide") return null;
  if (s.default_price == null) return null;
  const cur = s.default_currency || "INR";
  try {
    const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(s.default_price);
    return `${money} · ${(s.billing_method || s.pricing_type || "per_device").replace(/_/g, " ")}`;
  } catch { return `${cur} ${s.default_price}`; }
}

export default function ServiceGroup() {
  const { group } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | featured | premium

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/services/groups"),
      api.get("/services", { params: { group } }),
    ]).then(([g, s]) => {
      const m = (g.data.items || []).find((it) => it.key === group);
      setMeta(m || { key: group, label: group, hint: "" });
      setServices(s.data.items || []);
    }).finally(() => setLoading(false));
  }, [group]);

  const shown = services.filter((s) => {
    if (filter === "featured") return s.is_featured;
    if (filter === "premium") return s.is_premium;
    return true;
  });

  return (
    <div className="space-y-6 max-w-[1500px]">
      <div>
        <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="svcgroup-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to services
        </Link>
        <div className="mt-3 flex items-baseline gap-3">
          <h1 className="font-heading text-[28px] font-semibold text-slate-900 tracking-tight">{meta?.label || "Services"}</h1>
          <span className="text-xs text-slate-500">{services.length} service{services.length === 1 ? "" : "s"}</span>
        </div>
        {meta?.hint && <p className="text-sm text-slate-500 mt-1">{meta.hint}</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "All" },
          { key: "featured", label: "Featured" },
          { key: "premium", label: "Premium" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors " +
              (filter === f.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")
            }
            data-testid={`svcgroup-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="st-skeleton h-28 rounded-md" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center" data-testid="svcgroup-empty">
          <div className="text-sm font-medium text-slate-800">No services match</div>
          <div className="text-xs text-slate-500 mt-1">Try switching filter or ask Suntronix to add a service.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {shown.map((s) => {
            const price = priceLabel(s);
            return (
              <button
                key={s.code}
                onClick={() => navigate(`/services/detail/${s.code}`)}
                className="text-left bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 hover:shadow-sm transition-[box-shadow,border-color] group"
                data-testid={`svcgroup-card-${s.code}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading text-base font-semibold text-slate-900">
                        {s.customer_facing_name || s.name}
                      </div>
                      {s.is_premium && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[color:var(--st-accent)] bg-[color:var(--st-accent-soft)] rounded-full px-2 py-0.5">
                          <Sparkles className="h-3 w-3" /> Premium
                        </span>
                      )}
                      {s.is_featured && !s.is_premium && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-700 bg-slate-100 rounded-full px-2 py-0.5">
                          <Star className="h-3 w-3" /> Featured
                        </span>
                      )}
                    </div>
                    {s.short_description && (
                      <div className="mt-1 text-sm text-slate-600 leading-relaxed">{s.short_description}</div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                      {s.sla_target && (
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" strokeWidth={1.6} /> SLA {s.sla_target}</span>
                      )}
                      {s.approval_required && (
                        <span className="inline-flex items-center gap-1 text-slate-600"><ShieldCheck className="h-3 w-3" strokeWidth={1.6} /> Approval required</span>
                      )}
                      {price && <span className="font-mono text-slate-700">{price}</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 mt-1 shrink-0 group-hover:text-slate-800" strokeWidth={1.6} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
